import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, ArrowsClockwise, X, MapPin, WarningCircle } from "@phosphor-icons/react";
import { listLocations, filterLocations, groupByZone, clearLocationsCache } from "@/lib/inventoryApi";
import CapacityBar from "@/components/inventory/CapacityBar";

/**
 * Searchable location picker button — pair with any location text input.
 *
 * Behavior (matches mobile app):
 *   • Session-cached location list (opening the picker doesn't refetch every
 *     time — a manual "Refresh" button forces a reload).
 *   • Search is case-insensitive across location_code, location_name, zone.
 *   • Rows show code · name · zone · type + capacity bar (green/amber/red).
 *   • Free-form input on the parent is preserved — this is opt-in.
 *   • Fails gracefully with a red banner when Base44 is unavailable.
 */
export default function LocationPicker({
  value,
  onChange,
  buttonLabel = "Pick from list",
  testId = "location-picker",
}) {
  const [open, setOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const searchRef = useRef(null);

  const load = useCallback(async ({ force = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const cache = await listLocations({ force });
      setLocations(cache.locations);
      setTotal(cache.total);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load locations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      // Focus search after popover mount
      setTimeout(() => searchRef.current?.focus(), 60);
    }
  }, [open, load]);

  const refresh = useCallback(() => {
    clearLocationsCache();
    load({ force: true });
  }, [load]);

  const filtered = useMemo(() => filterLocations(locations, q), [locations, q]);
  const grouped = useMemo(() => groupByZone(filtered), [filtered]);

  const pick = (loc) => {
    onChange?.(loc.location_code, loc);
    setOpen(false);
    setQ("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 px-2.5 gap-1.5 font-semibold flex-shrink-0"
          data-testid={`${testId}-trigger`}
        >
          <MapPin size={16} weight="bold" />
          <span className="hidden sm:inline">{buttonLabel}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[92vw] max-w-md p-0 max-h-[75vh] flex flex-col"
        align="end"
        data-testid={`${testId}-popover`}
      >
        <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 flex items-center gap-2 flex-shrink-0">
          <div className="relative flex-1 min-w-0">
            <MagnifyingGlass size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search code, name, or zone…"
              className="pl-8 h-9"
              data-testid={`${testId}-search`}
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                aria-label="Clear search"
              >
                <X size={14} weight="bold" />
              </button>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={refresh}
            disabled={loading}
            className="h-9"
            data-testid={`${testId}-refresh`}
            title="Reload locations from Base44"
          >
            <ArrowsClockwise size={16} weight="bold" className={loading ? "animate-spin" : ""} />
          </Button>
        </div>

        {error ? (
          <ErrorBanner message={error} onRetry={refresh} />
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid={`${testId}-scroll`}>
            {loading && !locations.length ? (
              <div className="p-6 text-center text-sm text-zinc-500">Loading locations…</div>
            ) : !filtered.length ? (
              <div className="p-6 text-center text-sm text-zinc-500" data-testid={`${testId}-empty`}>
                {q ? `No locations match "${q}"` : "No active locations"}
              </div>
            ) : (
              <div className="py-1">
                {grouped.map(({ zone, locations: zoneLocs }) => (
                  <div key={zone}>
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold sticky top-0 bg-white dark:bg-zinc-900 z-10">
                      {zone}
                    </div>
                    {zoneLocs.map((loc) => (
                      <LocationRow
                        key={loc.location_code}
                        location={loc}
                        selected={value === loc.location_code}
                        onClick={() => pick(loc)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="px-3 py-2 border-t border-zinc-200 dark:border-zinc-800 text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-500 flex justify-between items-center flex-shrink-0">
          <span data-testid={`${testId}-count`}>{filtered.length} of {total}</span>
          {locations.length > 0 && (
            <span className="normal-case tracking-normal font-normal text-zinc-400">
              Cached · click ↻ to refresh
            </span>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function LocationRow({ location, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 border-b border-zinc-100 dark:border-zinc-800/60 last:border-b-0 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors ${
        selected ? "bg-red-50 dark:bg-red-950/30" : ""
      }`}
      data-testid={`location-row-${location.location_code}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono font-bold text-sm truncate" data-testid="location-row-code">
            {location.location_code}
          </div>
          <div className="text-xs text-zinc-700 dark:text-zinc-300 truncate">
            {location.location_name || "—"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mt-0.5">
            {location.zone || "—"} {location.location_type ? `· ${location.location_type}` : ""}
          </div>
        </div>
        <div className="w-24 flex-shrink-0">
          <CapacityBar location={location} compact />
        </div>
      </div>
    </button>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div
      className="p-4 flex items-start gap-2 text-sm bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-900"
      data-testid="location-picker-error"
    >
      <WarningCircle size={20} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="font-bold text-red-800 dark:text-red-200">Base44 unavailable</div>
        <div className="text-xs text-red-700 dark:text-red-300 mt-0.5 break-words">{message}</div>
        <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </div>
  );
}
